import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import { ArrowLeft, CheckCircle, XCircle, Clock, RotateCcw } from 'lucide-react';

interface EmailLog {
  id: string;
  recipient_email: string;
  subject: string;
  status: string;
  error_message: string | null;
  sent_at: string;
  sendgrid_message_id: string | null;
  batch_api_call_id: string | null;
  contact_id: string;
}

interface EmailDebuggerPanelProps {
  campaignId: string;
  campaignSubject: string;
  onBack: () => void;
}

export function EmailDebuggerPanel({
  campaignId,
  campaignSubject,
  onBack
}: EmailDebuggerPanelProps) {
  const [activeTab, setActiveTab] = useState<'all' | 'sent' | 'failed'>('all');
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<EmailLog[]>([]);

  useEffect(() => {
    loadEmailLogs();
  }, [campaignId]);

  useEffect(() => {
    filterLogs();
  }, [logs, activeTab]);

  const loadEmailLogs = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('email_send_logs')
        .select('id, recipient_email, subject, status, error_message, sent_at, sendgrid_message_id, batch_api_call_id, contact_id')
        .eq('campaign_id', campaignId)
        .order('sent_at', { ascending: false });

      if (error) throw error;

      setLogs(data || []);
    } catch (err) {
      console.error('Error loading email logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];

    if (activeTab === 'sent') {
      filtered = filtered.filter(log => log.status === 'sent');
    } else if (activeTab === 'failed') {
      filtered = filtered.filter(log => log.status === 'failed');
    }

    setFilteredLogs(filtered);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const sentCount = logs.filter(l => l.status === 'sent').length;
  const failedCount = logs.filter(l => l.status === 'failed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Campaign
        </button>
        <h3 className="font-medium text-gray-900">{campaignSubject}</h3>
        <p className="text-sm text-gray-500 mt-1">
          {logs.length} total emails • {sentCount} sent • {failedCount} failed
        </p>
      </div>

      <div className="border-b border-gray-200">
        <div className="flex gap-4 px-6">
          <button
            onClick={() => setActiveTab('all')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All ({logs.length})
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'sent'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Sent ({sentCount})
          </button>
          <button
            onClick={() => setActiveTab('failed')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'failed'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Failed ({failedCount})
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No emails found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className={`border rounded-lg p-4 ${
                  log.status === 'failed'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(log.status)}
                    <p className="font-medium text-gray-900">{log.recipient_email}</p>
                  </div>
                  <p className="text-xs text-gray-500">{formatDate(log.sent_at)}</p>
                </div>

                {log.error_message && (
                  <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-800">
                    {log.error_message}
                  </div>
                )}

                {log.sendgrid_message_id && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">
                      Message ID: <code className="text-xs bg-gray-100 px-1 rounded">{log.sendgrid_message_id}</code>
                    </p>
                  </div>
                )}

                {log.status === 'failed' && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => {
                        console.log('Retry single email:', log.id);
                      }}
                      className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Retry
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
